import { ThemeEvents } from '@theme/events';

/*
 * Frontend-only Recharge replacement UI.
 * Recharge/Shopify selling plans remain the backend source of truth.
 */
const SELECTORS = {
    comparePrice: '[data-js="subscription-selector-compare-price"]',
    currentPrice: '[data-js="subscription-selector-current-price"]',
    root: '[data-js="subscription-selector"]',
    input: '[data-js="subscription-selector-input"]',
    mode: '[data-js="subscription-selector-mode"]',
    note: '[data-js="subscription-selector-note"]',
    planButton: '[data-js="subscription-selector-plan-button"]',
    price: '[data-js="subscription-selector-price"]',
    select: '[data-js="subscription-selector-select"]',
};

let hasInitialized = false;
const observedForms = new WeakSet();

const CHANGE_HANDLERS = new Map([
    [`${SELECTORS.mode}, ${SELECTORS.select}`, handleSelectorControlChange],
]);

const CLICK_HANDLERS = new Map([
    [SELECTORS.planButton, handlePlanButtonClick],
]);

/**
 * Finds the product form owned by the same section as the subscription selector.
 * @param {HTMLElement} root
 * @returns {HTMLFormElement | null}
 */
function getProductForm(root) {
    const sectionId = root.dataset.sectionId;

    if (sectionId) {
        const sectionForm = document.querySelector(
            `product-form-component[data-section-id="${CSS.escape(sectionId)}"] form[data-type="add-to-cart-form"]`
        );

        if (sectionForm instanceof HTMLFormElement) {
            return sectionForm;
        }
    }

    const productDetails = root.closest('[data-testid="product-information-details"]');
    const localForm = productDetails?.querySelector('product-form-component form[data-type="add-to-cart-form"]');

    return localForm instanceof HTMLFormElement ? localForm : null;
}

/**
 * @param {HTMLFormElement} form
 * @returns {HTMLElement | null}
 */
function getSelectorForForm(form) {
    for (const root of document.querySelectorAll(SELECTORS.root)) {
        if (root instanceof HTMLElement && getProductForm(root) === form) {
            return root;
        }
    }

    return null;
}

/**
 * @param {HTMLFormElement} form
 */
function observeProductForm(form) {
    if (observedForms.has(form)) return;

    form.addEventListener('formdata', handleProductFormData);
    observedForms.add(form);
}

/**
 * Associates the selling_plan input with the live product form.
 * Recharge remains the subscription backend; this only submits Shopify's selling_plan value.
 * @param {HTMLElement} root
 */
function syncFormBinding(root) {
    const input = root.querySelector(SELECTORS.input);
    const form = getProductForm(root);

    if (!(input instanceof HTMLInputElement) || !form?.id) return;

    input.setAttribute('form', form.id);
    observeProductForm(form);
}

/**
 * @param {HTMLElement} root
 * @returns {HTMLInputElement | null}
 */
function getSelectedModeInput(root) {
    const selectedMode = root.querySelector(`${SELECTORS.mode}:checked`);
    return selectedMode instanceof HTMLInputElement ? selectedMode : null;
}

/**
 * @param {HTMLElement} root
 * @returns {HTMLSelectElement | HTMLButtonElement | HTMLOptionElement | null}
 */
function getSelectedPlanControl(root) {
    const selectedButton = root.querySelector(`${SELECTORS.planButton}[aria-pressed="true"]`);
    if (selectedButton instanceof HTMLButtonElement && !selectedButton.disabled) {
        return selectedButton;
    }

    const select = root.querySelector(SELECTORS.select);
    if (!(select instanceof HTMLSelectElement)) return null;
    const selectedOption = select.selectedOptions[0];

    if (selectedOption && !selectedOption.disabled) {
        return selectedOption;
    }

    return Array.from(select.options).find((option) => !option.disabled) || null;
}

/**
 * @param {HTMLElement} root
 * @returns {string}
 */
function getSelectedPlanId(root) {
    const selectedControl = getSelectedPlanControl(root);

    if (selectedControl instanceof HTMLButtonElement) {
        return selectedControl.dataset.planId || '';
    }

    if (selectedControl instanceof HTMLOptionElement) {
        return selectedControl.value;
    }

    return '';
}

/**
 * @param {HTMLElement} root
 * @param {string} planId
 */
function selectPlan(root, planId) {
    const buttons = Array.from(root.querySelectorAll(SELECTORS.planButton));
    for (const button of buttons) {
        if (!(button instanceof HTMLButtonElement)) continue;

        button.setAttribute('aria-pressed', button.dataset.planId === planId ? 'true' : 'false');
    }

    const select = root.querySelector(SELECTORS.select);
    if (!(select instanceof HTMLSelectElement)) return;

    const option = Array.from(select.options).find((item) => item.value === planId && !item.disabled);
    if (option) {
        select.value = option.value;
    }
}

/**
 * Keeps the subscribe card price/copy aligned to the selected selling plan.
 * @param {HTMLElement} root
 */
function updateSubscriptionCopy(root) {
    const selectedControl = getSelectedPlanControl(root);
    if (!selectedControl) return;

    const comparePrice = root.querySelector(SELECTORS.comparePrice);
    const currentPrice = root.querySelector(SELECTORS.currentPrice);
    const price = root.querySelector(SELECTORS.price);
    const note = root.querySelector(SELECTORS.note);

    if (currentPrice && selectedControl.dataset.price) {
        currentPrice.textContent = selectedControl.dataset.price;
    } else if (price && selectedControl.dataset.price) {
        price.textContent = selectedControl.dataset.price;
    }

    if (comparePrice) {
        comparePrice.textContent = selectedControl.dataset.comparePrice || '';
        comparePrice.hidden = !selectedControl.dataset.comparePrice;
    }

    if (note && selectedControl.dataset.note) {
        note.textContent = selectedControl.dataset.note;
    }
}

/**
 * @param {HTMLElement} root
 * @param {'one-time' | 'subscription'} mode
 */
function selectMode(root, mode) {
    const modeInput = root.querySelector(`${SELECTORS.mode}[value="${mode}"]`);

    if (modeInput instanceof HTMLInputElement && !modeInput.disabled) {
        modeInput.checked = true;
    }
}

/**
 * Syncs visible selector state to the hidden selling_plan input.
 * @param {HTMLElement} root
 */
function updateSelectorState(root) {
    syncFormBinding(root);

    const input = root.querySelector(SELECTORS.input);
    const select = root.querySelector(SELECTORS.select);
    const buttons = root.querySelectorAll(SELECTORS.planButton);
    const selectedModeInput = getSelectedModeInput(root);
    const selectedMode = selectedModeInput?.value === 'subscription' ? 'subscription' : 'one-time';
    const isSubscription = selectedMode === 'subscription';

    root.dataset.state = isSubscription ? 'subscription' : 'one-time';

    if (select instanceof HTMLSelectElement) {
        select.disabled = !isSubscription;
    }

    for (const button of buttons) {
        if (button instanceof HTMLButtonElement) {
            button.disabled = !isSubscription;
        }
    }

    const selectedPlanId = getSelectedPlanId(root);

    updateSubscriptionCopy(root);

    if (!(input instanceof HTMLInputElement)) return;

    if (isSubscription && selectedPlanId) {
        input.disabled = false;
        input.value = selectedPlanId;
    } else {
        input.value = '';
        input.disabled = true;
    }
}

/**
 * Ensures the product form payload matches the selected subscription UI state.
 * @param {FormDataEvent} event
 */
function handleProductFormData(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const root = getSelectorForForm(form);
    if (!root) return;

    updateSelectorState(root);

    event.formData.delete('selling_plan');

    if (root.dataset.state !== 'subscription') return;

    const selectedPlanId = getSelectedPlanId(root);
    if (selectedPlanId) {
        event.formData.set('selling_plan', selectedPlanId);
    }
}

/**
 * @param {Event} event
 */
function handleSelectorControlChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const root = target.closest(SELECTORS.root);
    if (!(root instanceof HTMLElement)) return;

    updateSelectorState(root);
}

/**
 * @param {Event} event
 */
function handleSelectorChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    for (const [selector, handler] of CHANGE_HANDLERS) {
        if (!target.closest(selector)) continue;

        handler(event);
        break;
    }
}

/**
 * @param {Event} event
 */
function handlePlanButtonClick(event) {
    event.preventDefault();

    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const button = target.closest(SELECTORS.planButton);
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const root = button.closest(SELECTORS.root);
    if (!(root instanceof HTMLElement)) return;

    selectMode(root, 'subscription');
    selectPlan(root, button.dataset.planId || '');
    updateSelectorState(root);
}

/**
 * @param {Event} event
 */
function handleSelectorClick(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    for (const [selector, handler] of CLICK_HANDLERS) {
        if (!target.closest(selector)) continue;

        handler(event);
        break;
    }
}

/**
 * Replaces this widget with the freshly rendered variant-aware markup.
 * @param {CustomEvent} event
 */
function handleVariantUpdate(event) {
    const html = event.detail?.data?.html;
    if (!(html instanceof Document)) return;

    for (const root of document.querySelectorAll(SELECTORS.root)) {
        if (!(root instanceof HTMLElement)) continue;
        if (event.detail?.data?.productId && root.dataset.productId !== event.detail.data.productId) continue;

        const sectionSelector = `product-form-component[data-section-id="${CSS.escape(root.dataset.sectionId || '')}"]`;
        if (!html.querySelector(sectionSelector)) continue;

        const selectedMode = getSelectedModeInput(root)?.value || '';
        const selectedPlanId = getSelectedPlanId(root);
        const blockSelector = [
            SELECTORS.root,
            `[data-section-id="${CSS.escape(root.dataset.sectionId || '')}"]`,
            `[data-block-id="${CSS.escape(root.dataset.blockId || '')}"]`,
        ].join('');
        const newRoot = html.querySelector(blockSelector);

        if (!(newRoot instanceof HTMLElement)) {
            root.remove();
            continue;
        }

        const clonedRoot = /** @type {HTMLElement} */ (newRoot.cloneNode(true));
        root.replaceWith(clonedRoot);

        if (selectedMode === 'one-time') {
            selectMode(clonedRoot, 'one-time');
        } else if (selectedPlanId) {
            selectMode(clonedRoot, 'subscription');
            selectPlan(clonedRoot, selectedPlanId);
        }

        updateSelectorState(clonedRoot);
    }
}

function hydrateSelectors() {
    for (const root of document.querySelectorAll(SELECTORS.root)) {
        if (root instanceof HTMLElement) {
            syncFormBinding(root);
        }
    }
}

function initSubscriptionSelectors() {
    if (hasInitialized) {
        hydrateSelectors();
        return;
    }

    document.addEventListener('change', handleSelectorChange);
    document.addEventListener('click', handleSelectorClick);
    document.addEventListener(ThemeEvents.variantUpdate, handleVariantUpdate);
    hasInitialized = true;

    hydrateSelectors();
}

initSubscriptionSelectors();
